import { FC, useContext, useEffect, useMemo } from 'react';
import { createGQLDataContext } from 'utils/createDataContext';
import { useGQLQuery } from 'hooks';
import { GQLAPIContextType } from 'types/common.types';
import {
  FetchTieringDetailsQuery,
  BiddingTime,
  FetchTieringDetailsQueryVariables,
} from 'types/graphql';
import { useLocation } from 'react-router-dom';
import { UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';
import { QUERY_shiftDistributionRulesTieringDetails } from 'queries';
import { getBiddingOvertimeDefault } from 'utils/shiftFormEvents';

type ShiftDistributionData = {
  locationName: string;
  biddingTimes: Nullable<BiddingTime[]>;
  biddingConfiguration?: {
    conflictEmploymentStatuses: string[];
    biddingOvertimeDefault: boolean;
  };
};

export const fetchShiftDistributionLocationConfig = (
  locationId: number,
): UseGQLQueryConfig<
  FetchTieringDetailsQuery,
  ShiftDistributionData,
  FetchTieringDetailsQueryVariables,
  FetchTieringDetailsQueryVariables
> => {
  return {
    key: 'SHIFT_DISTRIBUTION_RULES_MODAL_LOCATION_QUERY',
    query: QUERY_shiftDistributionRulesTieringDetails,
    variables: {
      id: locationId,
    },
    responseTransformer: (data: FetchTieringDetailsQuery): ShiftDistributionData => {
      const biddingConfiguration = data?.fetchCommunity?.biddingConfiguration;
      return {
        locationName: data && data.fetchCommunity ? data.fetchCommunity.name : '',
        biddingTimes: data?.fetchCommunity?.biddingTimes?.length
          ? [...data.fetchCommunity.biddingTimes].sort((a, b) => a.priorityTime - b.priorityTime)
          : null,
        biddingConfiguration: {
          conflictEmploymentStatuses: biddingConfiguration?.conflictEmploymentStatuses || [],
          biddingOvertimeDefault: getBiddingOvertimeDefault(biddingConfiguration),
        },
      } as ShiftDistributionData;
    },
  };
};

export const ShiftDistributionLocationDataContext =
  createGQLDataContext<GQLAPIContextType<ShiftDistributionData>>();

export const ShiftDistributionLocationDataProvider: FC = ({ children }) => {
  const { pathname } = useLocation();
  const locationId = pathname.split('/')[3];

  const { data, fetch } = useGQLQuery<FetchTieringDetailsQuery, ShiftDistributionData, {}>(
    fetchShiftDistributionLocationConfig(Number.parseInt(locationId)),
  );

  useEffect(() => {
    if (locationId) {
      fetch();
    }
  }, []); // eslint-disable-line

  const contextValues = useMemo(() => {
    return {
      locationName: data?.locationName,
      biddingTimes: data?.biddingTimes,
      biddingConfiguration: data?.biddingConfiguration,
      fetch,
    };
  }, [data, fetch]);

  return (
    <ShiftDistributionLocationDataContext.Provider value={contextValues}>
      {children}
    </ShiftDistributionLocationDataContext.Provider>
  );
};

export const useShiftDistributionLocationDataContext =
  (): GQLAPIContextType<ShiftDistributionData> => useContext(ShiftDistributionLocationDataContext);
